import React, { useEffect, useMemo, useState } from 'react';
import {
  FiCheck,
  FiClock,
  FiPackage,
  FiRefreshCw,
  FiSave,
  FiSettings,
} from 'react-icons/fi';
import { api } from '../services/api';
import './HomeProductShowcaseSettings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEFAULT_FORM = {
  enabled: true,
  changeTime: '09:00',
  timeZone: 'Asia/Tehran',
  category: 'all',
  sortOrder: 'createdAtAsc',
  startProductId: '',
};

const getId = (item) => String(item?._id || item?.id || '');

const resolveMediaUrl = (value) => {
  const source = String(value || '').trim();
  return source.startsWith('/') ? `${API_URL}${source}` : source;
};

const formatDateTime = (value, timeZone) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('fa-IR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }).format(date);
  } catch (_) {
    return date.toLocaleString('fa-IR');
  }
};

const HomeProductShowcaseSettings = ({ products = [], categories = [] }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/api/products/showcase/settings');
        if (!cancelled && response?.success && response.data) {
          setForm({ ...DEFAULT_FORM, ...response.data.settings });
          setPreview(response.data.preview || null);
        }
      } catch (error) {
        if (!cancelled) {
          setFeedback({
            type: 'error',
            text: error.data?.message || error.message || 'دریافت تنظیمات نمایش محصول انجام نشد',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const eligibleProducts = useMemo(() => products.filter((product) => (
    form.category === 'all' || product.category === form.category
  )), [form.category, products]);

  useEffect(() => {
    if (loading || eligibleProducts.length === 0) return;
    if (!eligibleProducts.some((product) => getId(product) === String(form.startProductId))) {
      setForm((current) => ({ ...current, startProductId: getId(eligibleProducts[0]) }));
    }
  }, [eligibleProducts, form.startProductId, loading]);

  const handleCategoryChange = (event) => {
    const category = event.target.value;
    const firstProduct = products.find((product) => category === 'all' || product.category === category);
    setForm((current) => ({
      ...current,
      category,
      startProductId: getId(firstProduct),
    }));
    setFeedback({ type: '', text: '' });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback({ type: '', text: '' });
    try {
      const response = await api.put('/api/products/showcase/settings', form);
      if (response?.success && response.data) {
        setForm({ ...DEFAULT_FORM, ...response.data.settings });
        setPreview(response.data.preview || null);
        setFeedback({ type: 'success', text: response.message || 'تنظیمات با موفقیت ذخیره شد' });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error.data?.message || error.message || 'ذخیره تنظیمات انجام نشد',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentImage = resolveMediaUrl(
    preview?.currentProduct?.cardImageUrl || preview?.currentProduct?.imageUrl
  );

  return (
    <section className={`home-product-showcase-settings ${form.enabled ? 'is-enabled' : 'is-disabled'}`} aria-labelledby="home-product-showcase-settings-title">
      <div className="home-product-showcase-settings__header">
        <div className="home-product-showcase-settings__title">
          <span><FiSettings aria-hidden="true" /></span>
          <div>
            <h3 id="home-product-showcase-settings-title">نمایش محصول در صفحه خانه</h3>
            <p>زمان و محصولات چرخهٔ روزانه را از همین بخش مدیریت کنید.</p>
          </div>
        </div>
        <label className="home-product-showcase-settings__switch">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            disabled={loading || saving}
          />
          <span aria-hidden="true" />
          <strong>{form.enabled ? 'فعال' : 'متوقف'}</strong>
        </label>
      </div>

      <form onSubmit={handleSave} className="home-product-showcase-settings__form">
        <div className="home-product-showcase-settings__field">
          <label htmlFor="showcase-change-time">ساعت تعویض</label>
          <div className="home-product-showcase-settings__input-with-icon">
            <FiClock aria-hidden="true" />
            <input
              id="showcase-change-time"
              type="time"
              value={form.changeTime}
              onChange={(event) => setForm((current) => ({ ...current, changeTime: event.target.value }))}
              disabled={loading || saving}
              required
            />
          </div>
        </div>

        <div className="home-product-showcase-settings__field">
          <label htmlFor="showcase-time-zone">منطقه زمانی</label>
          <select
            id="showcase-time-zone"
            value={form.timeZone}
            onChange={(event) => setForm((current) => ({ ...current, timeZone: event.target.value }))}
            disabled={loading || saving}
          >
            <option value="Asia/Tehran">تهران</option>
            <option value="Asia/Baku">باکو</option>
            <option value="Europe/Berlin">برلین</option>
            <option value="UTC">UTC</option>
          </select>
        </div>

        <div className="home-product-showcase-settings__field">
          <label htmlFor="showcase-category">دسته‌بندی</label>
          <select
            id="showcase-category"
            value={form.category}
            onChange={handleCategoryChange}
            disabled={loading || saving}
          >
            <option value="all">همه محصولات</option>
            {categories.map((category) => (
              <option key={getId(category) || category.name} value={category.name}>{category.name}</option>
            ))}
          </select>
        </div>

        <div className="home-product-showcase-settings__field">
          <label htmlFor="showcase-sort-order">ترتیب چرخه</label>
          <select
            id="showcase-sort-order"
            value={form.sortOrder}
            onChange={(event) => setForm((current) => ({ ...current, sortOrder: event.target.value }))}
            disabled={loading || saving}
          >
            <option value="createdAtAsc">قدیمی به جدید</option>
            <option value="createdAtDesc">جدید به قدیمی</option>
            <option value="nameAsc">نام؛ الف تا ی</option>
            <option value="nameDesc">نام؛ ی تا الف</option>
          </select>
        </div>

        <div className="home-product-showcase-settings__field home-product-showcase-settings__field--product">
          <label htmlFor="showcase-start-product">محصول فعلی / شروع چرخه</label>
          <select
            id="showcase-start-product"
            value={form.startProductId}
            onChange={(event) => setForm((current) => ({ ...current, startProductId: event.target.value }))}
            disabled={loading || saving || eligibleProducts.length === 0}
          >
            {eligibleProducts.length === 0 ? (
              <option value="">محصولی در این دسته‌بندی وجود ندارد</option>
            ) : eligibleProducts.map((product) => (
              <option key={getId(product)} value={getId(product)}>{product.name}</option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="home-product-showcase-settings__save"
          disabled={loading || saving || eligibleProducts.length === 0}
        >
          {saving ? <FiRefreshCw className="is-spinning" aria-hidden="true" /> : <FiSave aria-hidden="true" />}
          {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
        </button>
      </form>

      <div className="home-product-showcase-settings__status">
        <div className="home-product-showcase-settings__current">
          <span className="home-product-showcase-settings__thumb">
            <FiPackage aria-hidden="true" />
            {currentImage && <img src={currentImage} alt="" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
          </span>
          <div>
            <small>محصول در حال نمایش</small>
            <strong>{preview?.currentProduct?.name || (loading ? 'در حال دریافت...' : 'محصولی انتخاب نشده')}</strong>
          </div>
        </div>
        <div className="home-product-showcase-settings__next">
          <span><FiClock aria-hidden="true" /></span>
          <div>
            <small>تعویض بعدی</small>
            <strong>{formatDateTime(preview?.nextChangeAt, form.timeZone)}</strong>
          </div>
        </div>
        <div className="home-product-showcase-settings__cycle">
          <span><FiRefreshCw aria-hidden="true" /></span>
          <div>
            <small>وضعیت چرخه</small>
            <strong>
              {preview?.totalProducts
                ? `${Number(preview.currentPosition || 0).toLocaleString('fa-IR')} از ${Number(preview.totalProducts).toLocaleString('fa-IR')}`
                : 'چرخه خالی است'}
            </strong>
          </div>
        </div>
      </div>

      {preview?.nextProduct?.name && (
        <p className="home-product-showcase-settings__hint">
          <FiCheck aria-hidden="true" /> محصول بعدی در چرخه: <strong>{preview.nextProduct.name}</strong>
        </p>
      )}
      {feedback.text && (
        <p className={`home-product-showcase-settings__feedback is-${feedback.type}`}>{feedback.text}</p>
      )}
    </section>
  );
};

export default HomeProductShowcaseSettings;
